import { forwardRef, useCallback } from 'react';

import useTranslation from '@/utils/hooks/translation/useTranslation';

import { classNames } from '@/utils/classNames';

import Icon from '@/components/base/Icon';

type SparksInputProps = {
    value: string;
    onChange: (value: string) => void;
    onSend: () => void;
    disabled?: boolean;
};

export const SparksInput = forwardRef<HTMLTextAreaElement, SparksInputProps>(
    ({ value, onChange, onSend, disabled = false }, ref) => {
        const { t } = useTranslation('sparks');
        const canSend = value.trim().length > 0 && !disabled;

        const handleSend = () => onSend();

        const handleKeyDown = useCallback(
            (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                if (e.nativeEvent.isComposing) return;
                if (e.key === 'Enter' && !e.shiftKey && canSend) {
                    e.preventDefault();
                    handleSend();
                }
            },
            [canSend, onSend]
        );

        return (
            <div className="shrink-0 px-3.5">
                <div className="flex flex-col justify-between overflow-hidden rounded-lg border border-icoGray-300 bg-white p-3 shadow-icoXS dark:border-icoDarkMode-beton dark:bg-icoDarkMode-anthracite">
                    <textarea
                        ref={ref}
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={t('input_placeholder')}
                        rows={2}
                        disabled={disabled}
                        className={classNames(
                            'w-full resize-none border-none bg-transparent py-2 pl-0 pr-2 font-GMedium text-xs tracking-tight outline-none ring-0 focus:outline-none focus:ring-0',
                            'text-icoBlack placeholder:text-icoGray-500 dark:text-white dark:placeholder:text-icoDarkMode-wolf'
                        )}
                    />
                    <div className="flex items-center justify-end">
                        {/* Coming soon (v2) */}
                        {/* <button
                            type="button"
                            className="flex items-center justify-center text-icoGray-500 transition-colors hover:text-icoBlack">
                            <Icon icon="plus" className="text-[14px]" />
                        </button> */}
                        <button
                            type="button"
                            onClick={handleSend}
                            disabled={!canSend}
                            className={classNames(
                                'flex size-6 items-center justify-center rounded-full transition-colors',
                                canSend
                                    ? 'bg-icoBlue text-white dark:bg-icoBlue'
                                    : 'cursor-not-allowed bg-icoGray-300 text-icoGray-500 dark:bg-icoDarkMode-deepBlack dark:text-icoDarkMode-wolf'
                            )}>
                            <Icon icon="arrow-top" className="text-[14px]" />
                        </button>
                    </div>
                </div>
            </div>
        );
    }
);

SparksInput.displayName = 'SparksInput';
